import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  HamburgerMenuLink,
  HeaderLogo,
  ContactUsLink,
  ChoosePlanType,
} from 'organisms';
import {
  useMembersPlan,
  useMyFeatureAttribute,
  useFeaturePermission,
  useMemberEligibility,
} from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext, isAvMed } from 'context';
import Logout from '../logout/Logout';
import {
  UserDetails,
  errorhandler,
  NavigateConst,
  deleteEncryptedValue,
  isGuest,
  isWellnessUser,
  WELLNESS_PLAN_CONST,
  DeviceTypes,
  useDeviceType,
  setEncryptedValue,
  getDecryptedValue,
  safeJsonParse,
} from 'common';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFeatureAttribute, MyFeatureAttributeResponse } from 'pages';
import {
  Loader,
  Link,
  DetailedAlertMessages,
} from '@sentara-health/optimahealth_web';
import LanguageDropdown from 'organisms/languageDropdown/LanguageDropdown';
import { tagOpenReplayMemberId } from 'common/openreplay/openreplay';

const Header: React.FC<any> = () => {
  const { MyFeatureAttributeType } = useMyFeatureAttribute();
  const { getFeaturePermission } = useFeaturePermission();
  const { MemberEligibilityRequest } = useMemberEligibility();
  const { t, i18n } = useTranslation();
  const {
    setServiceNumber,
    getFeature,
    userEncodedData,
    setUserEncodedData,
    setAge,
    loginMemberId,
    setPrimaryUser,
    setPlanName,
    setPlanWellness,
    setLoginMID,
    setLoginMemberId,
    loginMID,
    profileState,
    setOnEmailUpdate,
    setHealthEquityHSA,
  } = useContext(GlobalContext);
  const { membersPlan } = useMembersPlan();
  const [planList, setPlanList] = useState<
    {
      isDefault: boolean;
      planName: string;
      planID: string | number;
      memberID: string | number;
      benefitPhone1800: string;
      benefitPhoneLocal: string;
    }[]
  >([]);
  const [selectedplanType, setSelectedplanType] = useState('');
  const [memberID, setMemberID] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestValue, setGuestValue] = useState<boolean>(false);
  const [wellnessValue, setWellnessValue] = useState<boolean>(false);
  const eligibilityChecked = useRef(false);
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const location = useLocation();

  const emailBounceBackFeature = getFeature?.EmailBounceBack ?? false;
  const multiLanguageFeature = getFeature?.MultiLanguage ?? false;

  useEffect(() => {
    const savedLanguage = localStorage.getItem('userLanguagePreference');
    if (
      getFeature?.MultiLanguage &&
      i18n.language !== 'en-US' &&
      !savedLanguage
    ) {
      // Only force English if user hasn't explicitly chosen a language
      i18n.changeLanguage('en-US');
    } else if (savedLanguage && savedLanguage !== i18n.language) {
      // Restore user's saved preference
      i18n.changeLanguage(savedLanguage);
    }
  }, [getFeature?.MultiLanguage, i18n]);

  useEffect(() => {
    (async () => {
      const isGuestUser: boolean = await isGuest();
      //call api and show member services number if LD toggle on
      if (getFeature?.MemberServicesNumbers && !isGuestUser) {
        const currentObject = planList?.filter(
          (item: { isDefault: boolean }) => item.isDefault === true
        );

        const phoneNumbers = isAvMed()
          ? {
              data: {
                benefitPhone1800: t('resetPassword.contactSupportNo'),
                benefitPhoneLocal: null,
              },
            }
          : {
              data: {
                benefitPhone1800: currentObject[0]?.benefitPhone1800,
                benefitPhoneLocal: null,
              },
            };
        setServiceNumber(JSON.stringify(phoneNumbers));
      }
    })();
  }, [getFeature?.MemberServicesNumbers, planList]);

  useEffect(() => {
    if (eligibilityChecked.current) {
      return;
    }

    eligibilityChecked.current = true;

    (async () => {
      // For non-AvMed, always set to true
      if (!isAvMed()) {
        setHealthEquityHSA(true);
        return;
      }

      // For AvMed, check eligibility via API
      const guestValue: boolean = await isGuest();
      if (guestValue || !loginMemberId) {
        return;
      }
      const eligibilityResponse = await MemberEligibilityRequest('', {
        identifier: [
          {
            system: 'memberId',
            value: loginMemberId,
          },
        ],
      });

      const benefits =
        eligibilityResponse?.data?.insuranceplans?.[0]?.items?.benefit;
      if (benefits) {
        const healthEquityHSA = benefits.find(
          (benefit: any) => benefit?.type?.coding?.code === 'HealthEquity_HSA'
        );
        setHealthEquityHSA(healthEquityHSA?.value === 'Y');
      }
    })();
  }, [loginMemberId]);

  const getMembersPlan = async () => {
    const response: any = await membersPlan();
    if (response?.data) {
      setPlanList(response?.data);
      const currentObject = response?.data?.filter(
        (item: { isDefault: boolean }) => item.isDefault === true
      );
      setSelectedplanType(currentObject[0]?.planName);
      setMemberID(currentObject[0]?.mId);
      setPrimaryUser(currentObject[0]?.relationCode);
      setPlanName(currentObject[0]?.planName);
      setEncryptedValue(UserDetails.memberIdData, currentObject[0]?.mId);
    } else if (response?.errorCode) {
      errorhandler(response?.errorCode);
    }
  };
  const getFeaturePermissionAPI = async () => {
    setLoading(true);
    const response = await getFeaturePermission();
    if (response?.error) {
      setLoading(false);
    } else {
      const decodedMemberId = atob(response?.data?.memberId ?? '');
      setLoginMID(response?.data?.mId ?? '');
      setLoginMemberId(decodedMemberId);
      void tagOpenReplayMemberId(decodedMemberId);
    }
  };

  useEffect(() => {
    (async () => {
      const guestValue: boolean = await isGuest();
      if ((loginMID === '' || loginMID === undefined) && !guestValue) {
        getFeaturePermissionAPI();
      }
    })();
  }, [loginMID]);

  useEffect(() => {
    (async () => {
      const guestValue: boolean = await isGuest();
      setGuestValue(guestValue);
      if (!guestValue) {
        getMembersPlan();
      } else {
        setPlanList([
          {
            isDefault: true,
            planName: t('common.guest'),
            planID: '',
            memberID: '',
            benefitPhone1800: '',
            benefitPhoneLocal: '',
          },
        ]);
        setSelectedplanType(t('common.guest') ?? '');
      }
    })();
  }, []);

  //getLaunchDarkly api to navigate to the dashboard screen
  const getLaunchDarkly = async () => {
    const response = await MyFeatureAttributeType();
    if (response?.errorCode !== undefined) {
      localStorage.clear();
      deleteEncryptedValue();
      window.location.href = NavigateConst.signIn;
    } else {
      const personId = String(
        safeJsonParse(await getDecryptedValue(UserDetails.id), '')
      );
      setUserEncodedData(
        getFeatureAttribute(
          response?.data as MyFeatureAttributeResponse,
          personId
        )
      );
      setAge(response?.data?.age);
    }
    setLoading(false);
  };

  // To call getLaunchDarkly API once userencodedata is empty
  useEffect(() => {
    (async () => {
      const guestValue: boolean = await isGuest();
      if (!guestValue && userEncodedData?.includes('anonymous')) {
        getLaunchDarkly();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const wellnessValue: boolean = await isWellnessUser();
      setWellnessValue(wellnessValue);
      if (getFeature?.WellnessPrograms && wellnessValue) {
        const wellnessPlanName = t('common.wellness');
        const wellnessPlanConst = WELLNESS_PLAN_CONST;

        setPlanList([
          {
            isDefault: true,
            planName: wellnessPlanName,
            planID: '',
            memberID: '',
            benefitPhone1800: '',
            benefitPhoneLocal: '',
          },
        ]);
        setSelectedplanType(wellnessPlanName);
        setPlanName(wellnessPlanName);
        setPlanWellness(wellnessPlanConst);
      }
    })();
  }, [getFeature?.WellnessPrograms, setPlanName, setPlanWellness]);

  const shouldShowicon = true;

  const setCookie = () => {
    document.cookie = `auth_token=${localStorage.getItem(
      UserDetails.authToken
    )}`;
  };
  useEffect(() => {
    return setCookie();
  }, []);

  const shouldShowContactUs =
    getFeature?.ContactUs &&
    ((getFeature?.WellnessPrograms && wellnessValue) || !guestValue);

  return (
    <>
      {loading && <Loader />}
      {/* Skip to Main Content link */}
      <Link to="#main-content" className="skip-link">
        {t('common.skipToMainContent')}
      </Link>
      <header className="not-print">
        <header
          data-testid="header"
          className={`${
            deviceType === DeviceTypes.DESKTOP ? 'border-bottom' : ''
          }`}
        >
          <div className="container-fluid">
            <nav className="header container-lg d-flex align-items-center justify-content-between">
              <HeaderLogo />
              <div className="d-flex flex-no-wrap align-items-center">
                {shouldShowContactUs && (
                  <div className="d-none d-xl-block d-lg-block d-md-none px-4">
                    <ContactUsLink
                      memberID={loginMID}
                      showIcon={shouldShowicon}
                    />
                  </div>
                )}
                {multiLanguageFeature && (
                  <div className="d-none d-xl-block d-lg-block d-md-none position-relative pe-4">
                    <LanguageDropdown />
                  </div>
                )}
                <div className="d-lg-block d-none">
                  <Logout />
                </div>
                <div className="d-lg-none d-block">
                  <HamburgerMenuLink showLogout />
                </div>
              </div>
            </nav>
          </div>
          {planList.length > 0 && (
            <main
              className={`container-fluid py-1 ${(() => {
                if (deviceType === DeviceTypes.MOBILE) {
                  return 'border-top border-bottom';
                } else if (deviceType === DeviceTypes.TABLET) {
                  return 'border-top';
                } else {
                  return '';
                }
              })()}`}
            >
              <div className="container-lg px-sub d-flex justify-content-xl-end justify-content-lg-end justify-content-md-end justify-content-center my-2">
                <ChoosePlanType
                  selectedplanType={selectedplanType}
                  planList={planList}
                  setSelectedplanType={setSelectedplanType}
                  memberID={memberID}
                />
              </div>
            </main>
          )}
        </header>
      </header>
      {/* && profileState?.memberId */}
      {profileState?.isEmailValid === false &&
        profileState?.memberId &&
        emailBounceBackFeature &&
        location?.pathname === '/dashboard' && (
          <div className="container-lg px-main">
            <DetailedAlertMessages
              actions={[
                {
                  title: t('common.updateEmailBtn'),
                  to: '/profile',
                  onClick: () => setOnEmailUpdate(true),
                },
              ]}
              additionalInfo=" "
              dismissible="true"
              message={t('common.emailNotUpdatedError')}
              variant="warning"
              type="alert"
            />
          </div>
        )}
    </>
  );
};
export default Header;
